import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, niceName } from "@/lib/format";
import { fmtVal } from "@/lib/docReasons";
import { Input } from "@/components/ui/input";
import { AiPanel } from "@/components/AiPanel";
import { CasaPronta } from "@/components/CasaPronta";

function ResolveControls({ check, canResolve, justify, onResolve, onReopen, busy }) {
  const [resolving, setResolving] = useState(false);
  const [reason, setReason] = useState("");
  const resolved = check.status === "resolvido";
  if (resolved) {
    return (
      <div className="mt-2 text-[11px] text-slate-500">
        {check.false_alarm ? "Falso alarme" : justify ? "Justificado" : "Resolvido"} por {check.resolved_by_name} · {fmtDate(check.resolved_at)}
        {check.reason && check.reason !== "Falso alarme" ? ` · ${check.reason}` : ""}
        {canResolve && <button onClick={onReopen} className="ml-2 text-ec-green underline hover:no-underline" data-testid={`reopen-${check.id}`}>reabrir</button>}
      </div>
    );
  }
  if (!canResolve || check.blocking) return null;
  if (resolving) {
    return (
      <div className="mt-2.5 flex items-center gap-2">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={justify ? "Justificação obrigatória (ex.: confirmado com a conservatória)" : "Porque está resolvido? (quem/porquê)"}
          className="h-8 text-xs flex-1" data-testid={`resolve-reason-${check.id}`} />
        <button onClick={async () => { if (await onResolve(false, reason)) { setResolving(false); setReason(""); } }} disabled={busy || (justify && !reason.trim())} className="h-8 px-3 rounded-lg bg-ec-green text-white text-xs font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-50" data-testid={`resolve-confirm-${check.id}`}>
          {busy ? "A guardar…" : "Confirmar"}
        </button>
        <button onClick={() => setResolving(false)} className="h-8 px-2 text-xs text-slate-400">Cancelar</button>
      </div>
    );
  }
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2">
      <button onClick={() => setResolving(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors" data-testid={`resolve-${check.id}`}>
        <CheckCircle2 size={13} /> {justify ? "Justificar" : "Resolver"}
      </button>
      <button onClick={() => onResolve(true, reason)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors" data-testid={`false-alarm-${check.id}`}>
        Falso alarme
      </button>
    </div>
  );
}

function CheckItem({ pid, check, onChange }) {
  const { user } = useAuth();
  const canResolve = user?.role === "gestor";
  const [busy, setBusy] = useState(false);
  const resolved = check.status === "resolvido";
  const justify = (check.rule_id || "").startsWith("cadastro_");
  const isProblema = check.severity === "problema";

  const doResolve = async (falseAlarm, reason) => {
    setBusy(true);
    try {
      await api.post(`/processes/${pid}/checks/resolve`, { check_id: check.id, reason: falseAlarm ? reason || "Falso alarme" : reason, false_alarm: falseAlarm });
      toast.success(falseAlarm ? "Marcado como falso alarme. Obrigado, ajuda a afinar as verificações." : justify ? "Aviso justificado" : "Aviso resolvido");
      onChange();
      return true;
    } catch (e) { toast.error("Não foi possível resolver"); return false; } finally { setBusy(false); }
  };
  const doReopen = async () => {
    await api.post(`/processes/${pid}/checks/reopen`, { check_id: check.id });
    toast.success("Aviso reaberto");
    onChange();
  };
  const applyFix = async (fx) => {
    if (fx.action === "unassign") { window.dispatchEvent(new CustomEvent("doc-move", { detail: fx.args.key })); return; }
    try { await api.post(`/processes/${pid}/apply-fix`, { action: fx.action, args: fx.args }); toast.success("Corrigido"); onChange(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const Icon = isProblema ? AlertCircle : AlertTriangle;
  const color = resolved ? "#64748b" : isProblema ? "#b91c1c" : "#b45309";

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={`check-${check.id}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={15} className="shrink-0 mt-0.5" style={{ color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{check.title}</span>
            {resolved && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500">{check.false_alarm ? "Falso alarme" : justify ? "Justificado" : "Resolvido"}</span>}
          </div>
          {check.detail && <p className="text-xs text-slate-600 mt-1">{check.detail}</p>}
          {(check.value_a || check.value_b) && (
            <div className="mt-1.5 text-xs text-slate-600 space-y-0.5">
              {[check.value_a, check.value_b].filter(Boolean).map((v, i) => (
                <div key={i}><span className="text-slate-400">{v.label}:</span> <span className="font-medium text-slate-700 break-words">{fmtVal(v.value) || "—"}</span></div>
              ))}
            </div>
          )}
          {check.excerpt && <div className="text-[11px] text-slate-400 italic mt-1.5">“{check.excerpt}”</div>}
          {check.source && <div className="text-[11px] text-slate-500 mt-1" data-testid={`check-source-${check.id}`}>Origem: {check.source}</div>}
          {check.legal && !resolved && <div className="text-[11px] font-medium mt-1.5" style={{ color }}>Confirmar com quem titula o ato.</div>}
          {(check.fix || check.fixes) && !resolved && user?.role !== "direccao" && (
            <div className="mt-2 flex flex-wrap gap-2">
              {[...(check.fix ? [check.fix] : []), ...(check.fixes || [])].map((fx, i) => (
                <button key={fx.action} onClick={() => applyFix(fx)}
                  className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${i === 0 ? "bg-ec-green text-white hover:bg-ec-greenDark" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`} data-testid={`check-fix-${check.id}-${fx.action}`}>{fx.label}</button>
              ))}
            </div>
          )}
          <ResolveControls check={check} canResolve={canResolve} justify={justify} onResolve={doResolve} onReopen={doReopen} busy={busy} />
        </div>
      </div>
    </div>
  );
}

function ChecksBlock({ pid, doc, onChange }) {
  const [showResolved, setShowResolved] = useState(false);
  const checks = doc.checks || [];
  if (checks.length === 0) return null;
  const open = checks.filter((c) => c.status === "aberto");
  const paused = checks.filter((c) => c.status === "pausa");
  const resolved = checks.filter((c) => c.status === "resolvido");
  return (
    <div className="mt-3 space-y-2" data-testid={`checks-${doc.key}`}>
      {open.map((c) => <CheckItem key={c.id} pid={pid} check={c} onChange={onChange} />)}
      {paused.length > 0 && (
        <div className="space-y-2" data-testid={`paused-${doc.key}`}>
          <div className="text-xs text-slate-500">{paused.length} {paused.length === 1 ? "aviso em pausa" : "avisos em pausa"}: voltam a contar quando chegar o documento certo ou se confirmar que está certo.</div>
          {paused.map((c) => <div key={c.id} className="opacity-60"><CheckItem pid={pid} check={c} onChange={onChange} /></div>)}
        </div>
      )}
      {resolved.length > 0 && (
        <button type="button" onClick={() => setShowResolved(!showResolved)} data-testid={`resolved-toggle-${doc.key}`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ec-green transition-colors">
          <ChevronDown size={13} className={`transition-transform ${showResolved ? "rotate-180" : ""}`} />
          {resolved.length} {resolved.length === 1 ? "aviso resolvido" : "avisos resolvidos"}
        </button>
      )}
      {showResolved && resolved.map((c) => <CheckItem key={c.id} pid={pid} check={c} onChange={onChange} />)}
    </div>
  );
}

const SENT_CH = { whatsapp: "WhatsApp", email: "email", copy: "mensagem copiada" };

// Detalhe de um documento no Dossier: envios, nota, leitura da IA e avisos.
export function DocRow({ pid, doc, onChange }) {
  const ro = useAuth().user?.role === "direccao";
  return (
    <div className="pt-1" data-testid={`doc-${doc.key}`}>
      {doc.cpcv_ref && (
        <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200" data-testid={`cpcv-ref-${doc.key}`}>
          Referido no CPCV, falta o documento · {doc.cpcv_ref}
        </span>
      )}
      {(doc.sends || []).length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid={`doc-sends-${doc.key}`}>
          {doc.sends.slice(-5).reverse().map((s, i) => (
            <div key={i} className="text-[11px] text-slate-500">
              {s.kind === "lembrete" ? "Lembrete" : "Enviado"} por {SENT_CH[s.channel] || s.channel} ao {s.recipient === "condominio" ? "condomínio" : s.recipient}
              {s.to_name ? ` (${s.to_name})` : ""} · {fmtDate(s.at)} · {s.by?.startsWith("Lembrete") ? s.by : niceName(s.by)}
            </div>
          ))}
        </div>
      )}
      {doc.note && <div className="text-[11px] text-slate-400 mt-1.5" data-testid={`doc-note-${doc.key}`}>{doc.note}</div>}
      <fieldset disabled={ro}>
        {doc.ai && !doc.ai.error
          ? <AiPanel pid={pid} doc={doc} onChange={onChange} checks={<ChecksBlock pid={pid} doc={doc} onChange={onChange} />} />
          : <><AiPanel pid={pid} doc={doc} onChange={onChange} /><ChecksBlock pid={pid} doc={doc} onChange={onChange} /></>}
        {doc.key === "direito_preferencia" && doc.status !== "nao_aplica" && <CasaPronta pid={pid} />}
      </fieldset>
    </div>
  );
}
